/**
 * Apple 登录服务
 * 使用 Apple Sign In JS SDK 实现弹窗式登录
 * 文档：https://developer.apple.com/documentation/signinwithapple/configuring-your-webpage-for-sign-in-with-apple#Embed-Sign-in-with-Apple-JS
 *
 * 安全特性：
 * - STATE 验证 - 防止 CSRF 攻击
 * - NONCE 验证 - 防止重放攻击
 */

import { APPLE_CONFIG } from '@/config/apple'
import i18n from '@/locales'
import {
  generateState,
  saveState,
  generateNonce,
  validateOAuthEnvironment,
  generateVerifier,
  generateChallenge,
  saveVerifier,
} from '@/utils/oauth'
import { OAUTH_PROVIDERS } from '@/common/constants'

const errorMessages = {
 'popup_closed_by_user': 'login.authCancelled',
 'popup_blocked': 'login.popupBlocked',
 'network_error': 'common.request.networkError',
 'user_cancelled_authorize': 'login.authCancelled',
}

const name = OAUTH_PROVIDERS.APPLE
const { t } = i18n.global

class AppleAuthService {
  constructor() {
    this.initialized = false
    this.initPromise = null
  }

  /**
   * 初始化 Apple Sign In JS SDK
   * @returns {Promise<void>}
   */
  async initialize() {
    // 如果已经初始化，直接返回
    if (this.initialized) {
      return
    }

    // 如果正在初始化，返回同一个 Promise
    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = new Promise((resolve, reject) => {
      // 检查是否已加载 Apple SDK
      if (window.AppleID) {
        this.initialized = true
        resolve()
        return
      }

      // 动态加载 Apple SDK 脚本
      const script = document.createElement('script')
      script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
      script.async = true
      script.defer = true

      script.onload = () => {
        this.initialized = true
        resolve()
      }

      script.onerror = () => {
        this.initPromise = null // 重置 Promise，允许重试
        reject(new Error(t('login.loadSDKFailed', { name })))
      }

      document.head.appendChild(script)
    })

    return this.initPromise
  }

  /**
   * 使用 Apple Sign In 登录（使用 JS SDK 弹窗方式）
   * @returns {Promise<{authorization: object}>} 返回授权响应
   */
  async login() {
    // 验证 OAuth 环境安全性
    validateOAuthEnvironment()
    try {
      console.log('[Apple OAuth] Attempting to login with JS SDK popup...')
      // 确保 SDK 已初始化
      await this.initialize()
      return await this.loginWithJsSDK()
    } catch (error) {
      console.error('[Apple OAuth] JS SDK login failed, falling back to Redirect mode:', error)
      // 回退到 Redirect 方式登录
      return await this.loginWithRedirect()
    }
  }

  /**
   * 使用 Apple Sign In 登录（使用 JS SDK 弹窗方式）
   * @returns {Promise<{authorization: object}>} 返回授权响应
   */
  async loginWithJsSDK() {
    try {
      // 确保 SDK 已初始化
      await this.initialize()

      // 生成 STATE 和 NONCE 参数
      const state = generateState()
      const nonce = generateNonce()
      // 初始化 Apple Sign In
      window.AppleID.auth.init({
        clientId: APPLE_CONFIG.clientId,
        scope: APPLE_CONFIG.scope,
        redirectURI: window.location.origin,
        // 需要告诉服务器，用户是从哪个域名过来的，Apple 只会透传 state，所以只能这么做
        state,
        nonce,
        usePopup: APPLE_CONFIG.usePopup, // 使用弹窗模式
        // usePopup: false // 使用Redirect模式
      })

      console.log('[Apple OAuth] Initiating authorization request')

      // 发起授权请求
      const data = await window.AppleID.auth.signIn()
      /**
       * https://developer.apple.com/documentation/signinwithapplejs/signinresponsei
       * data like: {
       *  authorization: {
       *    code: '',
       *    id_token: '',
       *    state: '',
       *  },
       *  // 仅首次授权时返回 user 信息
       *  user?: {
       *    email: '',
       *    name: {
       *      firstName: 'xxx',
       *      lastName: 'xxx',
       *    },
       *  },
       * }
       */

      // 如果 usePopup: false，即 Redirect 模式
      // 表现为：signIn 会立即 resolve，但 data 为 undefined
      // 此时页面会重定向，不应该继续执行后续代码
      // 返回一个永远不会 resolve 的 Promise，阻止后续代码执行，也不会触发 catch 中的 loginWithRedirect()
      if (!data) {
        console.log('[Apple OAuth] Redirect mode detected, page will redirect, stopping execution')
        return new Promise(() => {}) // 返回 pending Promise，阻止后续代码执行
      }

      // 验证 STATE（防止 CSRF 攻击）
      if (data?.authorization?.state) {
        const isValidState = state === data.authorization.state
        if (!isValidState) {
          return {
            error: t('login.stateValidationFailed')
          }
        }
      }

      console.log('[Apple OAuth] Authorization successful', data)

      // 返回授权数据
      // data.authorization 包含：code, id_token, state
      // data.user 包含：email, name (仅首次授权时返回)
      return {
        ...data,
        nonce,
      }
    } catch (error) {
      console.error('[Apple OAuth] Authorization failed:', error)
      const message = errorMessages[error.error]
      if (message) {
        return {
          error: t(message)
        }
      }
      // 其他情况抛出错误
      throw error
    }
  }

  /**
   * 使用 Redirect 方式登录（适用于某些浏览器环境）
   * 跳转到 Apple OAuth 授权页面，授权后重定向回来
   * @returns {Promise<never>} 不会返回，直接跳转页面
   */
  async loginWithRedirect() {
    // 1. 生成 PKCE 参数
    const codeVerifier = generateVerifier()
    const codeChallenge = await generateChallenge(codeVerifier)
    // 生成 STATE 和 NONCE 参数
    const state = generateState()

    // 保存到 sessionStorage
    saveVerifier(codeVerifier, OAUTH_PROVIDERS.APPLE)
    saveState(state, OAUTH_PROVIDERS.APPLE)

    // 构建授权 URL
    const authUrl = new URL('https://appleid.apple.com/auth/authorize')
    authUrl.searchParams.set('client_id', APPLE_CONFIG.clientId)
    authUrl.searchParams.set('redirect_uri', APPLE_CONFIG.redirectURI)
    authUrl.searchParams.set('response_type', APPLE_CONFIG.responseType)
    authUrl.searchParams.set('response_mode', APPLE_CONFIG.responseMode)
    authUrl.searchParams.set('scope', APPLE_CONFIG.scope)
    // 需要告诉服务器，用户是从哪个域名过来的，Apple 只会透传 state，所以只能这么做
    authUrl.searchParams.set('state', btoa(JSON.stringify({
      state,
      origin: `${window.location.origin}/apple-callback.html`,
    })))
    authUrl.searchParams.set('code_challenge', codeChallenge)
    authUrl.searchParams.set('code_challenge_method', 'S256')

    console.log('[Apple OAuth Redirect] Redirecting to authorization page:', authUrl.toString())

    // 跳转到 Apple 授权页面
    window.location.href = authUrl.toString()

    // 永远不会执行到这里，因为页面已经跳转
    return new Promise(() => {})
  }

  /**
   * 检查是否已初始化
   * @returns {boolean}
   */
  isInitialized() {
    return this.initialized
  }
}

// 导出单例
export default new AppleAuthService()
